import { EventType } from '@oneclick/analytics-sdk';
import { ROUTES } from 'app/utils/constants/paths';
import ChallengeTypeTag from 'core/chatbot/components/atoms/ChallengeTypeTag/ChallengeTypeTag';
import PracticeOriginLabel from 'core/chatbot/components/atoms/PracticeOriginLabel/PracticeOriginLabel';
import GeneratingSessionDialog from 'core/chatbot/components/compositions/CustomPracticeFlow/GeneratingSessionDialog';
import LayoutHeader from 'core/layout/LayoutHeader';
import { useUser } from 'core/lite/hooks/useUser';
import ProgressBar from 'core/modules/atoms/ProgressBar';
import HeaderCustom from 'core/modules/components/headers/HeaderCustom';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Icon, Loading } from 'ui';
import useAnalytics from '../../../hooks/useAnalytics';
import { useAIChatConversationActions } from '../../contexts/AIChatConversation/useAIChatConversationActions';
import { useAIChatShellActions } from '../../contexts/AIChatShell/useAIChatShellActions';
import useChatbotPracticeView from '../../hooks/useChatbotPracticeView';
import useReviewSessionGeneration from '../../hooks/useReviewSessionGeneration';
import practiceService from '../../services/practiceService';
import { mapContentTypeToChallenge, mapSessionTypeToOrigin } from '../../utils/challengeTypeUtils';
import ChatbotPracticeExercise from './ChatbotPracticeExercise';
import ChatbotPracticeSummary from './ChatbotPracticeSummary';
import ErrorReportModal from './ErrorReportModal';
import StatusModal from './StatusModal';
import './chatbot-practice-view.scss';

// Minimum progress percentage to avoid showing 0%
const MINIMUM_PROGRESS_PERCENTAGE = 2;

const ChatbotPracticeView = () => {
  const { t } = useTranslation(['chatbot']);
  const {
    isLoading,
    exercise,
    error,
    isChatAvailable,
    generateActivity,
    loadActivityInstance,
    activityInstanceId,
    activityId,
    completedActivitiesCount,
    isSessionComplete,
    activityResults,
    sessionKCs,
    resetPracticeSession,
    sessionId,
    urlActivityInstanceId,
    sessionData,
    activityExtraInfo, // ← TODO: Remove after testing

    // Error reporting state and actions
    isReportingError,
    showErrorModal,
    showStatusModal,
    statusModalType,
    handleReportError,
    handleSubmitError,
    handleCloseErrorModal,
    handleCloseStatusModal,
  } = useChatbotPracticeView();

  const { isGenerating: isReviewGenerating, hasError: hasReviewError, generateReviewSession, dismissError: dismissReviewError } = useReviewSessionGeneration();

  const { setCurrentConversationId, clearMessageQueue } = useAIChatConversationActions();
  const { closeChatbot } = useAIChatShellActions();
  const { user } = useUser();
  const { trackEvent } = useAnalytics();
  const hasSentCompletionEventRef = useRef(false);
  const lastStartedActivityInstanceIdRef = useRef(null);

  const history = useHistory();

  // Cierra el chat al entrar en la vista de práctica
  useEffect(function () {
    // Reset the conversation
    setCurrentConversationId(null);
    // Close the chat window
    closeChatbot();
  }, []);

  useEffect(() => {
    if (!activityInstanceId) return;
    if (lastStartedActivityInstanceIdRef.current === activityInstanceId) return;
    lastStartedActivityInstanceIdRef.current = activityInstanceId;
    practiceService.startActivityInstance(activityInstanceId);
  }, [activityInstanceId]);

  // Calculate progress percentage based on session data
  const progressPercentage = (completedActivitiesCount / Math.max(1, sessionData?.maxActivityNumber)) * 100 || MINIMUM_PROGRESS_PERCENTAGE;

  const handleActivityComplete = useCallback(
    (nextActivity) => {
      // Step 1: Clear message queue to prevent previous activity's messages from appearing
      clearMessageQueue();

      // Step 2: Close chatbot to prevent auto-send during transition
      closeChatbot();

      // Step 3: Clear the current chat conversation
      setCurrentConversationId(null);

      // Step 4: Navigate based on whether there's a next activity
      if (nextActivity) {
        // Navigate to the new activity URL - nextActivity doesn't have sessionId, use current sessionId
        const newUrl = `/activities/sessions/${sessionId}/activities/${nextActivity.id}`;
        history.replace(newUrl);
      } else {
        // No next activity - navigate to session summary
        const summaryUrl = `/activities/sessions/${sessionId}/summary`;
        history.replace(summaryUrl);
      }
    },
    [setCurrentConversationId, history, sessionId, clearMessageQueue, closeChatbot]
  );

  const handleExitPractice = useCallback(() => {
    // Reset practice session state before leaving
    resetPracticeSession();
    // Back to the student's "Today" space
    history.replace(ROUTES.TODAY);
  }, [resetPracticeSession, history]);

  const handleReview = useCallback(() => {
    generateReviewSession(sessionId);
  }, [generateReviewSession, sessionId]);

  useEffect(() => {
    // Check if we're on the summary route
    const isSummaryRoute = window.location.pathname.endsWith('/summary');

    if (isSummaryRoute && sessionId) {
      // Load session data for summary view
      loadActivityInstance(sessionId, null);
    } else if (sessionId && urlActivityInstanceId) {
      // Load specific activity instance from URL
      loadActivityInstance(sessionId, urlActivityInstanceId);
    } else if (!isSessionComplete) {
      // Only generate activity if we haven't completed the session and no URL params
      generateActivity();
    }
  }, [sessionId, urlActivityInstanceId, loadActivityInstance, generateActivity]);

  // Reset conversation and close chat when showing summary
  useEffect(() => {
    if (isSessionComplete) {
      // Reset the conversation
      setCurrentConversationId(null);
      // Close the chat window
      closeChatbot();
    }
  }, [isSessionComplete, setCurrentConversationId, closeChatbot]);

  // Send analytics event when session is completed (only once)
  useEffect(() => {
    if (isSessionComplete && sessionData && sessionKCs && !hasSentCompletionEventRef.current) {
      // Process KCs for the event
      const learnedKCs = sessionKCs.filter((kc) => kc.score > 0.9);
      const notMasteredKCs = sessionKCs.filter((kc) => kc.score <= 0.9);

      // Send analytics event for activity session completed
      trackEvent(EventType.ACTIVITY_SESSION_COMPLETED, {
        practice_session_id: sessionData.id,
        type: sessionData.sessionType,
        kcs_mastered: learnedKCs.map((kc) => kc.referenceId),
        kcs_not_mastered: notMasteredKCs.map((kc) => kc.referenceId),
        subject: sessionData.subject,
        number_activities: sessionData.maxActivityNumber,
      });

      // Mark as sent to avoid duplicates
      hasSentCompletionEventRef.current = true;
    }
  }, [isSessionComplete, sessionData, sessionKCs, trackEvent]);

  // Greet the student by first name only (user.name may carry the full name depending on the auth flow)
  const studentFirstName = user?.name?.split(' ')[0];

  return (
    <div className="ai-practice-view">
      <LayoutHeader
        header={
          <HeaderCustom
            left={
              <div className="ai-practice-view__header-left">
                {sessionData && (
                  <>
                    <PracticeOriginLabel origin={mapSessionTypeToOrigin(sessionData.sessionType)} />
                    {sessionData.contentType && <ChallengeTypeTag challengeType={mapContentTypeToChallenge(sessionData.contentType)} />}
                  </>
                )}
              </div>
            }
            center={
              <div className={`ai-practice-view__progress-container ${progressPercentage > 98 ? 'ai-practice-view__progress-container--100' : ''}`}>
                <Icon name="rayo" size="xl" />
                <ProgressBar progress={progressPercentage} size="medium" withLabel={false} />
              </div>
            }
            right={
              <div>
                {isSessionComplete ? (
                  <Button variant="primary" text={t('practice_button_done')} onClick={handleExitPractice} size="sm" isFullWidth />
                ) : (
                  <Button ariaLabel={t('common:Close')} variant="neutral-outline" leftIcon="close" isIconOnly onClick={handleExitPractice} isFullWidth />
                )}
              </div>
            }
          />
        }
      >
        <div className="ai-practice-view__content">
          {isSessionComplete ? (
            <ChatbotPracticeSummary
              firstName={studentFirstName}
              activityResults={activityResults}
              sessionKCs={sessionKCs}
              lessonTitle={sessionData?.lessonTitle}
              onReview={handleReview}
              isReviewGenerating={isReviewGenerating}
            />
          ) : (
            <>
              {isLoading && <Loading />}
              {error && (
                <div className="ai-practice-view__error">
                  <p>{t('practice_error_generate', { error })}</p>
                  <div>
                    <Button variant="primary" text={t('practice_button_try_again')} onClick={generateActivity} isFullWidth />
                  </div>
                </div>
              )}
              {!isLoading && !error && exercise && (
                <ChatbotPracticeExercise
                  key={activityInstanceId}
                  exercise={exercise}
                  isChatAvailable={isChatAvailable}
                  activityInstanceId={activityInstanceId}
                  onActivityComplete={handleActivityComplete}
                  activityExtraInfo={activityExtraInfo} // ← TODO: Remove after testing
                  isReportingError={isReportingError}
                  onReportError={handleReportError}
                  lessonTitle={sessionData?.lessonTitle}
                  sessionData={sessionData}
                />
              )}
            </>
          )}
        </div>
      </LayoutHeader>
      {/* Error Report Modal */}
      <ErrorReportModal
        open={showErrorModal}
        onClose={handleCloseErrorModal}
        onSubmit={handleSubmitError}
        exerciseId={exercise?.id || exercise?.data?.id}
        activityInstanceId={activityInstanceId}
        activityId={activityId}
      />
      {/* Status Modal (Success/Error) */}
      <StatusModal open={showStatusModal} onClose={handleCloseStatusModal} type={statusModalType} />
      {/* Review session generation (TANAI-1827): reuse the "Generando…" dialog and the status modal for errors */}
      <GeneratingSessionDialog isOpen={isReviewGenerating} />
      <StatusModal open={hasReviewError} onClose={dismissReviewError} type="error" />
    </div>
  );
};

export default ChatbotPracticeView;
